/// <reference path="../../types.d.ts" />

import { HangoutState, HANGOUT_EVENTS } from '../state/HangoutState.js';
import { reconstructActiveCharactersFromMessages } from '../shared/character-hydrate.js';
import { tokenManager } from '../../../js/utils/TokenManager.js';
import { storage } from '../../../js/utils/storage.js';
import { conversationQueue } from './conversation-queue.js';
import { $, create } from '../../../core/utils/dom.js';
import { getDisplayManager } from './hangout-display-manager.js';
import { getCurrentLayoutMode, isVnLayoutMode, LAYOUT_MODES } from './layout-mode.js';
import {
    showHangoutFreeAccountModal,
    showHangoutLoginModal,
    persistHangoutSessionBeforeLogin
} from './hangout-auth-modals.js';
import { cleanupAllAudioCache } from './voice-cache.js';
import { applyHangoutMemoryToRequestBody } from './hangout-memory-storage.js';
import {
    buildGreetingRequest,
    buildSpriteSelections,
    getSpriteSelectionOptions,
    isResolvedConversationId,
    buildTextGenRequestFields,
    enrichCharactersWithOutfits,
    filterRespondingCharacters,
    orderRespondingCharacters,
    speakingOrderIds,
    mapHangoutRequestCharacters
} from './request-builder.js';
import { resolveMessageSpriteData } from '../shared/sprite-state.js';
import { parseNumericId } from '../shared/id-utils.js';
import { compareNumericCharacterIds } from '../../utils/characterIdSort.js';
import { maybeShowHangoutTipToast } from './hangout-tips.js';
import { maybeShowHangoutFeatureToasts } from './hangout-feature-toasts.js';
import { buildFrontendStateDiff } from '../shared/frontend-state-diff.js';
import { resolveConversationHistory, getTranscriptForLayout } from '../shared/history-resolution.js';
import { clearConversationSpeakingClasses } from './mobile-handlers.js';
import { getBackgroundServiceInstance, getCurrentBackgroundSafely } from './hangout-background-access.js';
import { getHangoutConfig, setHangoutConfig } from '../shared/hangout-config-utils.js';
import { makeWorkerRequest, hangoutWorkerUrl } from './hangout-worker-request.js';
import { isMessageSendInFlight, setMessageSendInFlight } from './message-send-state.js';
import {
    beginStreamSession,
    getActiveStreamSession,
    endStreamSession,
    processStreamingResponse,
    settleFailedTurnBubbles
} from './message-stream.js';
import {
    refreshVnTurnNavigationFromLatestRound,
    syncLatestAssistantSpriteFromHistory,
    focusHangoutCharacterSprite,
    applyHistoricalHangoutScene,
    focusHangoutConversationTurn,
    getCharacterDisplayIndex,
    getRunningClockTime,
    resolvePlayableIsVideo
} from './hangout-scene.js';
import { resolveUserNameFromStorage } from '../../../js/utils/placeholder-resolver.js';
import {
    buildAssistantChainPaths,
    buildContinuationPathPlan,
    isDescendantPath,
    filterHistoryForRegenerate,
    filterHistoryForContinue,
    getPrecedingUserMessage,
    getRestOfTurnAssistants,
    getRoundAssistantMessages,
    getVersionMetaForPath,
    normalizeMessagePath,
    resolveCanonicalPath,
    resolveDbPathForLocalAssistant,
    resolveHistoricalSceneFromMessages,
    formatMilitaryAsClock,
    isGreetingMessage,
    isValidLtreePath,
    selectVersionCohort,
    resolveGreetingChainPath,
    greetingRootSegment,
    getPathRootSegment
} from '../shared/message-tree.js';

export { showHangoutFreeAccountModal, showHangoutLoginModal, cleanupAllAudioCache, resolveConversationHistory };
export { getDisplayManager };
export {
    refreshVnTurnNavigationFromLatestRound,
    focusHangoutCharacterSprite,
    applyHistoricalHangoutScene,
    focusHangoutConversationTurn,
    getCharacterDisplayIndex
};

let conversationRestorationHandlerInitialized = false;

const getChatMessagesContainer = () => {
    const chatContainer = $('.chat-container');
    if (!chatContainer) {
        console.error('Chat container not found');
        return null;
    }
    let chatHistory = chatContainer.querySelector('.chat-messages');
    if (!chatHistory) {
        chatHistory = create('div');
        chatHistory.className = 'chat-messages';
        chatContainer.appendChild(chatHistory);
    }
    return chatHistory;
};

function initializeConversationRestorationHandler() {
    if (conversationRestorationHandlerInitialized) return;

    try {
        const hangoutState = HangoutState.getInstance();

        hangoutState.subscribe(HANGOUT_EVENTS.CONVERSATION_RESUMED, async (eventData) => {
            if (eventData && eventData.shouldUpdateUI && eventData.lastMessage) {
                await restoreConversationUI(eventData.lastMessage, eventData.conversationId);
            }
        });

        conversationRestorationHandlerInitialized = true;
    } catch (error) {
        console.error('ConversationRestoration: Error setting up restoration handler:', error);
    }
}

// Restore conversation UI state after loading messages
async function restoreConversationUI(lastMessage, conversationId) {
    const displayManager = await getDisplayManager();
    if (getCurrentLayoutMode() === LAYOUT_MODES.CHATBOT) {
        const hangoutState = HangoutState.getInstance();
        const history = getTranscriptForLayout(hangoutState);
        await syncLatestAssistantSpriteFromHistory(lastMessage);
        await displayManager.syncHistory?.(history);
        return;
    }

    const updateCharacterSprite = async (character, message, characterIndex) => {
        try {
            return await syncLatestAssistantSpriteFromHistory(message, characterIndex);
        } catch (spriteError) {
            console.error('restoreConversationUI: Error updating sprite for character:', character.name, spriteError);
            return false;
        }
    };

    const createBubbleForCharacter = async (character, message, characterIndex) => {
        await updateCharacterSprite(character, message, characterIndex);
        const displayManager = await getDisplayManager();
        const resolvedSpriteData = resolveMessageSpriteData(message, {
            fallbackOutfitId: message.outfitId ?? 0
        });
        const spriteStateForBubble = {
            characterId: message.characterId,
            actionId: resolvedSpriteData.actionId,
            actionName: resolvedSpriteData.actionName,
            outfitId: resolvedSpriteData.outfitId,
            isVideo: resolvedSpriteData.isVideo
        };
        await displayManager.createMessage(
            message.content,
            message.characterName || character.name || 'Character',
            message.characterId,
            spriteStateForBubble
        );
    };

    // Clear ALL existing messages first to prevent duplicates
    await displayManager.clearActiveMessages();

    // Update character sprite to match the last message state
    if (lastMessage.characterId) {
        try {
            // Save current zoom states before updating character sprite
            const { saveAllZoomStates } = await import('./imageZoom.js');
            saveAllZoomStates();
            await syncLatestAssistantSpriteFromHistory(lastMessage);
        } catch (spriteError) {
            console.error('restoreConversationUI: Error updating character sprite:', spriteError);
        }
    }

    // Create speech bubbles for all active characters' last messages
    try {
        const hangoutState = HangoutState.getInstance();
        const allMessages = hangoutState.getMessages();

        if (allMessages && allMessages.length > 0) {
            // Get up-to-date active characters from config
            const hangoutConfig = await getHangoutConfig() || {};
            let activeCharacters = hangoutConfig.allCharacters || [];

            // If no active characters in config, reconstruct from message history
            if (activeCharacters.length === 0) {

                activeCharacters = await reconstructActiveCharactersFromMessages(allMessages, {
                    enrichCharacters: async (characterMap) => {
                        const { HangoutService } = await import('../services/HangoutService.js');
                        const allCharactersData = await HangoutService.getInstance().getAvailableCharacters();
                        const config = await getHangoutConfig();
                        for (const [charId, char] of characterMap.entries()) {
                            const fullCharacterData = allCharactersData.find((c) => c.id.toString() === charId);
                            if (!fullCharacterData) continue;
                            char.name = fullCharacterData.name;
                            if (char.outfitId === 0
                                && config.allCharacters?.[0]?.id?.toString() === charId
                                && config.allCharacters[0].outfitId) {
                                char.outfitId = config.allCharacters[0].outfitId;
                            }
                        }
                    }
                });

                // Update hangout config with reconstructed characters
                if (activeCharacters.length > 0) {
                    hangoutConfig.allCharacters = activeCharacters;

                    // Set primary character to the first one if not already set
                    if (!hangoutConfig.allCharacters?.[0]?.id && activeCharacters.length > 0) {
                        // This should not happen since we just set allCharacters above
                        console.warn('Primary character not found in allCharacters, this should not happen');
                    }

                    // Save updated config
                    await setHangoutConfig(hangoutConfig);

                }
            }

            // Find all characters that have sent messages (for validation)
            const charactersWithMessages = new Set();
            allMessages.forEach(msg => {
                if (msg.role === 'assistant' && msg.characterId) {
                    charactersWithMessages.add(String(msg.characterId));
                }
            });

            if (activeCharacters.length > 1) {
                for (const character of activeCharacters) {
                    const characterId = String(character.id);

                    if (!charactersWithMessages.has(characterId)) {
                        continue;
                    }

                    const lastCharacterMessage = allMessages
                        .slice()
                        .reverse()
                        .find(msg =>
                            msg.role === 'assistant' &&
                            String(msg.characterId) === characterId &&
                            msg.content
                        );

                    if (lastCharacterMessage) {
                        const characterIndex = activeCharacters.findIndex(c => String(c.id) === characterId);
                        await createBubbleForCharacter(character, lastCharacterMessage, characterIndex);
                    } else {
                        console.warn(`restoreConversationUI: No message found for character ${character.name} despite being in charactersWithMessages`);
                    }
                }
            } else if (activeCharacters.length === 1) {
                const character = activeCharacters[0];
                const characterId = String(character.id);

                if (charactersWithMessages.has(characterId) && lastMessage.content && lastMessage.characterId) {
                    await createBubbleForCharacter(character, lastMessage, 0);
                }
            } else {
                console.error('restoreConversationUI: No active characters found, using fallback bubble restoration');
                if (lastMessage.content && lastMessage.characterId) {
                    const fallbackCharacter = {
                        id: lastMessage.characterId,
                        name: lastMessage.characterName || 'Character',
                        outfitId: lastMessage.outfitId || 0
                    };
                    await createBubbleForCharacter(fallbackCharacter, lastMessage, 0);
                }
            }
        } else {
            console.warn('restoreConversationUI: No messages found for bubble restoration');
        }
    } catch (bubbleError) {
        console.error('restoreConversationUI: Error creating bubbles:', bubbleError);

        // Fallback: Create bubble for the last message only
        if (lastMessage.content && lastMessage.characterId) {
            // Create a fallback character object with default values
            const fallbackCharacter = {
                id: lastMessage.characterId,
                name: lastMessage.characterName || 'Character',
                outfitId: lastMessage.outfitId || 0
            };
            const characterIndex = 0; // Fallback to first character position
            await createBubbleForCharacter(fallbackCharacter, lastMessage, characterIndex);
        }
    }

    // Initialize VN turn navigation from the latest round — prefer latest speaker
    try {
        if (isVnLayoutMode()) {
            const preferredCharacterId = lastMessage?.characterId ?? lastMessage?.character_id ?? null;
            await refreshVnTurnNavigationFromLatestRound(preferredCharacterId);
        }
    } catch (navError) {
        console.warn('restoreConversationUI: Failed to initialize turn navigation:', navError);
    }
}

// display system message in chat history
function appendSystemMessage(message) {
    const chatHistory = getChatMessagesContainer();
    if (!chatHistory) {
        return;
    }

    const messageElement = document.createElement('div');
    messageElement.className = 'message system-message';

    messageElement.innerHTML = message.replace(/\n/g, '<br>');

    chatHistory.appendChild(messageElement);
    chatHistory.scrollTop = chatHistory.scrollHeight;

    const hangoutState = HangoutState.getInstance();
    hangoutState.addMessage({
        role: 'system',
        content: message
    }, false);
}

const refreshMessageRetryControls = async () => {
    try {
        const displayManager = await getDisplayManager();
        displayManager.redrawAllMessages?.();
    } catch (error) {
        console.warn('message-utils.js: Failed to refresh message retry controls:', error);
    }
};

const dropEmptyAssistantDescendants = (hangoutState, userPath) => {
    const currentMessages = hangoutState.getMessages() || [];
    const nextMessages = currentMessages.filter((entry) => {
        if (entry?.role !== 'assistant') return true;
        if (!isDescendantPath(entry.path, userPath)) return true;
        return String(entry.content || '').trim().length > 0;
    });
    if (nextMessages.length === currentMessages.length) return;
    hangoutState.setMessages(nextMessages);
};

export async function sendMessage(message, options = {}) {
    if (!message) return;
    if (isMessageSendInFlight()) return { inFlight: true };

    const hangoutState = HangoutState.getInstance();
    const existingUserPath = normalizeMessagePath(options?.existingUserPath);
    setMessageSendInFlight(true);
    await refreshMessageRetryControls();

    try {
        const currentSession = hangoutState.getCurrentSession();

        if (!tokenManager.isAuthenticated()) {
            console.info('message-utils.js: No auth token provided');

            await persistHangoutSessionBeforeLogin('send-message-unauth', { pendingMessage: message });

            await showHangoutFreeAccountModal('send-message-unauth');
            return { requiresAuth: true };
        }

        let conversationId = hangoutState.getConversationId();
        let skipPersistedUserInsert = false;
        let streamUserPath = existingUserPath;

        if (existingUserPath) {
            const currentMessages = hangoutState.getMessages() || [];
            const existingUserMessage = currentMessages.find((entry) =>
                entry?.role === 'user' && normalizeMessagePath(entry.path) === existingUserPath
            );
            if (!existingUserMessage) {
                return { success: false, error: 'Missing user message' };
            }
            skipPersistedUserInsert = existingUserMessage.persistedRemote === true;
            dropEmptyAssistantDescendants(hangoutState, existingUserPath);
        } else {
            // Assign the planned userPath before addMessage so provisional paths nest under tip.
            const earlyPathPlan = buildContinuationPathPlan(
                hangoutState.getMessages(),
                hangoutState.getActiveVersions(),
                1,
                hangoutState.getBranchFilterOptions?.() || {}
            );
            const addUserResult = await hangoutState.addMessage({
                role: 'user',
                content: message,
                ...(earlyPathPlan?.userPath ? { path: earlyPathPlan.userPath } : {})
            }, true);
            streamUserPath = normalizeMessagePath(addUserResult?.messagePath || earlyPathPlan?.userPath);
            if (getCurrentLayoutMode() === LAYOUT_MODES.CHATBOT) {
                const displayManager = await getDisplayManager();
                await displayManager.createUserMessage?.(message, {
                    path: streamUserPath
                });
            }
        }

        const streamSession = beginStreamSession({
            userPath: streamUserPath || null
        });
        if (streamSession?.id && streamUserPath) {
            const currentMessages = hangoutState.getMessages() || [];
            hangoutState.setMessages(currentMessages.map((entry) => {
                if (entry?.role !== 'user') return entry;
                if (normalizeMessagePath(entry.path) !== streamUserPath) return entry;
                return { ...entry, streamGenerationId: streamSession.id };
            }));
        }

        // Show contextual onboarding tips while the AI response is loading.
        // This call is intentionally non-blocking and should never interfere with message sending.
        void maybeShowHangoutTipToast(hangoutState);

        maybeShowHangoutFeatureToasts();

        // Ensure we have a scenario ID - get from storage config first, then fallback
        let scenarioId = currentSession?.scenarioId;
        if (!scenarioId) {
            // Try to get from storage config
            const hangoutConfig = await getHangoutConfig();
            scenarioId = hangoutConfig?.scenarioId;

            if (!scenarioId) {
                scenarioId = '1';
            }
        }

        const conversationHistory = await resolveConversationHistory({
            hangoutState
        });

        // Get current goal and location
        const selectedSpot = await hangoutState.getSelectedSpot();

        // Get active characters from storage config (same as updateCharacterImage)
        const hangoutConfig = await getHangoutConfig();
        let activeCharacters = hangoutConfig?.allCharacters || [];

        // CRITICAL FIX: If storage has no characters, try HangoutState before falling back
        // This prevents corrupting valid multi-character state saved by settings modal
        if (activeCharacters.length === 0) {
            console.warn('[sendMessage] No characters in storage config, checking HangoutState...');
            
            // Try HangoutState characters first (preserves settings modal selections)
            const stateChars = hangoutState.getCharacters();
            if (stateChars && stateChars.length > 0) {
                activeCharacters = stateChars;
            } else {
                // Fallback to selected character or recent character - but DO NOT save to storage
                // This prevents overwriting valid multi-character config that might be in storage
                // under a different key or timing issue
                const selectedChar = hangoutState.getSelectedCharacter();
                if (selectedChar) {
                    const characterForArray = {
                        id: selectedChar.id,
                        name: selectedChar.name,
                        outfitId: hangoutState.getSelectedOutfit() || 0,
                        goal: hangoutConfig?.goal || null
                    };

                    activeCharacters = [characterForArray];
                } else {
                    const recentChars = hangoutState.getRecentCharacters();
                    if (recentChars.length > 0) {
                        const characterForArray = {
                            id: recentChars[0].id,
                            name: recentChars[0].name,
                            outfitId: hangoutState.getSelectedOutfit() || 0,
                            goal: hangoutConfig?.goal || null
                        };

                        activeCharacters = [characterForArray];
                    } else {
                        console.warn('message-utils.js: No active, selected, or recent characters found.');
                        appendSystemMessage("No active character found. Please select a character first.");
                        return;
                    }
                }
            }
        }

        const currentTime = await getRunningClockTime();

        const outfitChanges = [];

        activeCharacters.forEach((char, index) => {
            const lastMessageForChar = conversationHistory
                .slice()
                .reverse()
                .find(msg =>
                    msg.role === 'assistant' &&
                    String(msg.characterId) === String(char.id)
                );

            const lastOutfitId = lastMessageForChar?.outfitId;
            const currentOutfitId = String(char.outfitId);
            const hasOutfitChanged = lastOutfitId && String(lastOutfitId) !== currentOutfitId;

            if (hasOutfitChanged) {
                char.outfitChanged = true;
                outfitChanges.push({
                    characterId: char.id,
                    oldOutfitId: lastOutfitId,
                    newOutfitId: currentOutfitId
                });

                // Add explicit system message about outfit change
                // Note: getSelectedBackground is async, but we're in a forEach so we can't await
                // Use a fallback for now to prevent syntax errors
                // TODO: get current background id before running forEach
                // can we store fewer metadata for this?
                const currentBackgroundId = '0';
                const outfitChangeMessage = {
                    role: 'system',
                    content: `${char.name} has changed their outfit from ${lastOutfitId} to ${currentOutfitId}.`,
                    path: `outfit_change_${Date.now()}_${char.id}`,
                    createdAt: Date.now(),
                    isOutfitChange: true,
                    characterId: char.id,
                    outfitId: currentOutfitId,
                    metadata: {
                        customScenarioDescription: hangoutState.getCustomScenarioDescription(),
                        currentGoal: char.goal || null,
                        timeOfDay: hangoutState.getCurrentTimeSlot() || 'night',
                        backgroundId: currentBackgroundId || 0,
                        backgroundUrl: selectedSpot?.backgroundUrl || 0,
                        scenarioId: scenarioId,
                        characterId: char.id,
                        outfitId: currentOutfitId,
                        customGoals: char.goal ? { [char.id]: char.goal } : {},
                        currentTime: currentTime,
                        outfitChange: {
                            characterId: char.id,
                            oldOutfitId: lastOutfitId,
                            newOutfitId: currentOutfitId
                        }
                    }
                };
                hangoutState.addMessage(outfitChangeMessage, false);
            }
        });

        const backgroundResult = await getCurrentBackgroundSafely(selectedSpot);
        const scene = resolveHistoricalSceneFromMessages(
            hangoutState.getVisibleMessages(),
            hangoutState.getBranchTipPath?.()
                || conversationHistory[conversationHistory.length - 1]?.path
        );
        const currentLocation = parseNumericId(scene.locationId)
            ?? parseNumericId(backgroundResult.locationId)
            ?? 0;
        const normalizedScenarioId = parseNumericId(scenarioId) ?? 1;

        await enrichCharactersWithOutfits(activeCharacters);

        // If auto voice is enabled, augment characters with voice ids and languages
        try {
            const autoVoiceEnabled = storage.get('autoVoiceEnabled') === 'true';
            if (autoVoiceEnabled) {
                const voiceIds = storage.get('characterVoiceIds') || {};
                const voiceLangs = storage.get('characterVoiceLanguages') || {};
                const isValidLang = (val) => typeof val === 'string' && /^[A-Za-z-]{2,5}$/.test(val);

                activeCharacters.forEach(char => {
                    const cid = String(char.id);
                    const vid = voiceIds[cid];
                    const vlang = voiceLangs[cid];
                    if (typeof vid === 'string' && vid.trim() !== '') {
                        char.voice_id = vid.trim();
                    }
                    if (isValidLang(vlang)) {
                        char.voice_language = vlang;
                    }
                });
            }
        } catch (e) {
            console.warn('Failed to augment characters with voice settings:', e);
        }

        // Get custom scenario description before building state diff
        let customScenarioDescription = null;
        try {
            const fromStorage = storage.get('hangout:customScenarioDescription');
            customScenarioDescription = fromStorage || null;
            if (typeof customScenarioDescription === 'string' && customScenarioDescription.trim() === '') {
                customScenarioDescription = null;
            }
        } catch (e) {
            console.warn('Failed to resolve custom scenario description:', e);
            customScenarioDescription = null;
        }

        // Get user data for rating
        const userData = storage.get('userData') || {};

        // Build state diff in frontend using the canonical worker key names.
        const stateDiff = await buildFrontendStateDiff(conversationHistory, {
            location: currentLocation,
            currentTime: currentTime,
            characters: activeCharacters,
            scenarioId: normalizedScenarioId,
            scenarioDescription: (typeof customScenarioDescription === 'string' && customScenarioDescription.trim() !== '')
                ? customScenarioDescription.trim()
                : undefined,
        });

        // Convert existing bubbles to typing mode before sending request (after auth check)
        const configForTyping = await getHangoutConfig();
        const charsForTyping = configForTyping?.allCharacters || [];

        // VN turn navigation (current turn only): determine which characters should respond this turn.
        const turnCharacters = orderRespondingCharacters(
            message,
            filterRespondingCharacters(message, activeCharacters)
        );

        // Always create typing indicators for responding characters
        if (charsForTyping && charsForTyping.length > 0) {
            try {
                const displayManager = await getDisplayManager();
                if (displayManager.isAvailable()) {
                    // Seed VN turn nav with expected responders; refreshed from the round as replies land.
                    if (isVnLayoutMode() && typeof displayManager.setTurnNavigation === 'function') {
                        displayManager.setTurnNavigation(turnCharacters);
                    }
                    const currentMessages = displayManager.getActiveMessages();

                    // Only show typing indicators for characters that should respond this turn.
                    const typingTargets = (turnCharacters && turnCharacters.length > 0) ? turnCharacters : charsForTyping;
                    for (const character of typingTargets) {
                        const charId = character.id || character.characterId;
                        const charName = character.name || character.characterName;

                        if (charId) {
                            // Always check display manager for current bubble state (character.bubble may be stale)
                            const existingMessage = displayManager.findMessageByCharacterId(charId) || character.bubble;
                            const existingText = String(existingMessage?.text || '').trim();
                            const reuseEmptyChatbotBubble = !isVnLayoutMode()
                                && existingMessage
                                && existingMessage.role !== 'user'
                                && existingMessage.isGreeting !== true
                                && typeof existingMessage.setText === 'function'
                                && (existingText === '' || existingText === '...');

                            if ((isVnLayoutMode() || reuseEmptyChatbotBubble)
                                && existingMessage
                                && existingMessage.role !== 'user'
                                && typeof existingMessage.setText === 'function') {
                                // Convert existing message to typing mode
                                existingMessage.isTyping = true;
                                existingMessage.isAwaitingDecision = true;
                                existingMessage.setText('...');
                                if (typeof existingMessage.markDirty === 'function') {
                                    existingMessage.markDirty();
                                }

                                // CRITICAL: Reset voice button state when entering typing mode
                                // Old audio is no longer relevant for the new response
                                existingMessage.voiceButtonState = 'play';
                                existingMessage.isAudioPlaying = false;
                                existingMessage.currentVoiceKey = null;
                            } else {
                                // Create new message with typing animation if none exists
                                const message = await displayManager.createTypingIndicator(charId, charName, {
                                    streamGenerationId: getActiveStreamSession()?.id || null
                                });
                                if (message) {
                                    message.isTyping = true;
                                    message.isAwaitingDecision = true;
                                    if (typeof message.markDirty === 'function') {
                                        message.markDirty();
                                    }
                                }
                            }
                        }
                    }
                }
            } catch (error) {
                console.warn('Failed to handle typing indicators:', error);
            }

            // Start animation loop for typing indicators using display manager
            try {
                const displayManager = await getDisplayManager();
                displayManager.startAnimation();
                displayManager.redrawAllMessages();
            } catch (error) {
                console.warn('Failed to start message animation:', error);
            }
        }

        const spriteSelections = buildSpriteSelections(activeCharacters);
        const spriteSelectionOptions = getSpriteSelectionOptions();

        const textGenFields = await buildTextGenRequestFields();
        // `/message` contract: send only fields consumed by worker runtime/state/prompt logic.
        /** @type {any} */
        const requestBody = {
            message,
            scenarioId: normalizedScenarioId,
            usernameForPrompt: resolveUserNameFromStorage({ preserveToken: true, fallback: '{user}' }),
            history: conversationHistory.filter(msg => msg.role !== 'system'),
            location: currentLocation,
            // Send the pre-built state diff
            stateDiff: stateDiff,
            ...textGenFields,
            characters: mapHangoutRequestCharacters({
                characters: activeCharacters,
                conversationHistory,
                message
            }),
            speakingOrder: speakingOrderIds(turnCharacters),
            ...(outfitChanges.length > 0 && { outfitChanges }),
            language: userData.language || 'en',
            greetingVariation: hangoutState.getGreetingVariationMeta().variation,
            isPrivateScenario: configForTyping?.isPrivateScenario === true,
            greetingSeedMessages: (() => {
                const variation = hangoutState.getGreetingVariationMeta().variation;
                const selectedRoot = greetingRootSegment(variation);
                return (hangoutState.getMessages() || [])
                    .filter((entry) => isGreetingMessage(entry)
                        && getPathRootSegment(entry?.path) === selectedRoot)
                    .map((message) => ({
                        content: message.content,
                        characterId: message.characterId ?? message.character_id,
                        path: message.path,
                        outfitId: message.outfitId ?? null,
                        actionId: message.actionId ?? null
                    }));
            })(),
            ...(spriteSelections.length > 0 && { spriteSelections }),
            spriteSelectionOptions
        };

        if (isResolvedConversationId(conversationId)) {
            requestBody.conversationId = conversationId.trim();
        }

        const respondingCount = turnCharacters.length;
        if (existingUserPath) {
            const assistantPaths = buildAssistantChainPaths(
                existingUserPath,
                Math.max(1, respondingCount),
                hangoutState.getMessages()
            );
            requestBody.pathPlan = {
                userPath: existingUserPath,
                assistantPaths
            };
            if (skipPersistedUserInsert) requestBody.skipUserMessage = true;
        } else {
            const pathPlan = buildContinuationPathPlan(
                hangoutState.getMessages().slice(0, -1),
                hangoutState.getActiveVersions(),
                Math.max(1, respondingCount),
                hangoutState.getBranchFilterOptions?.() || {}
            );
            if (pathPlan?.userPath) {
                const messages = [...hangoutState.getMessages()];
                const lastIndex = messages.length - 1;
                const lastMessage = messages[lastIndex];
                if (lastMessage?.role === 'user') {
                    const previousPath = normalizeMessagePath(lastMessage.path);
                    const nextPath = normalizeMessagePath(pathPlan.userPath);
                    if (previousPath !== nextPath) {
                        messages[lastIndex] = { ...lastMessage, path: pathPlan.userPath };
                        hangoutState.setMessages(messages);
                        await syncChatbotVisibleHistory();
                    }
                }
                requestBody.pathPlan = {
                    userPath: pathPlan.userPath,
                    assistantPaths: pathPlan.assistantPaths
                };
            }
        }

        // Add custom scenario description if exists
        if (customScenarioDescription) {
            // Custom scenario description included
            requestBody.scenarioDescription = customScenarioDescription;
        }

        applyHangoutMemoryToRequestBody(requestBody, hangoutConfig?.allCharacters || activeCharacters);

        // Log request details
        const workerUrl = hangoutWorkerUrl('/message');

        // Send to backend
        let response;
        try {
            response = await makeWorkerRequest(workerUrl, requestBody);
        } catch (error) {
            if (error.message === 'Authentication required') {
                await settleFailedTurnBubbles();
                return { requiresAuth: true };
            }

            // Check if quota error was handled
            if (error.message === 'QUOTA_HANDLED' || error.isQuotaHandled) {
                await settleFailedTurnBubbles();
                // Quota modal was shown, don't add error message to chat
                return { quotaExceeded: true };
            }

            throw error;
        }

        // Handle streaming response
        let data;

        data = await processStreamingResponse(response, activeCharacters, getBackgroundServiceInstance, {
            isInitialGreeting: false,
            streamGenerationId: streamSession?.id || null,
            streamUserPath: streamSession?.userPath || null
        });
        hangoutState.syncBranchTipFromVisible?.();

        if (conversationQueue.isManualPacing) {
            await conversationQueue.markStreamingComplete();
        }

        // Clean up mobile-specific classes after all responses are complete
        // For manual pacing, let the conversation queue handle cleanup to avoid premature cleanup
        // Cleanup should run across the full mobile/tablet breakpoint (including landscape)
        // to prevent `.conversation-active` from ever sticking.
        const isMobileViewport = window.innerWidth <= 1024;
        const isManualPacing = conversationQueue && conversationQueue.isManualPacing;
        if (isMobileViewport && !isManualPacing && isVnLayoutMode()) {
            clearConversationSpeakingClasses();
        }
        return data;
    } catch (error) {
        console.error('message-utils.js: sendMessage: Error sending message to Grok 3:', error);
        await settleFailedTurnBubbles();
        throw error;
    } finally {
        setMessageSendInFlight(false);
        endStreamSession();
        await refreshMessageRetryControls();
    }
}

export async function resendUserTurn(userPath) {
    const hangoutState = HangoutState.getInstance();
    const normalizedPath = normalizeMessagePath(userPath);
    if (!normalizedPath) return { success: false, error: 'Missing user path' };
    const userMessage = (hangoutState.getMessages() || []).find((entry) =>
        entry?.role === 'user' && normalizeMessagePath(entry.path) === normalizedPath
    );
    const content = String(userMessage?.content || '').trim();
    if (!content) return { success: false, error: 'Missing user message' };
    return sendMessage(content, { existingUserPath: normalizedPath });
}

// Generate initial greeting for new conversations using the worker greeting endpoint
export async function generateInitialGreeting(conversationId, scenarioId, characters, config) {
    try {
        const hangoutState = HangoutState.getInstance();

        // Use config character data directly - it has already been enriched with individual character details
        // including correct gender from createCharacterObject
        const currentSession = hangoutState.getCurrentSession();

        let scenarioStartTime = null;

        // Check if this is a promotional scenario with startTime
        const hangoutConfig = await getHangoutConfig();
        try {
            const promotionalScenario = hangoutConfig.selectedScenario;

            if (promotionalScenario && promotionalScenario.startTime) {
                scenarioStartTime = promotionalScenario.startTime;
            } else if (config && config.startTime) {
                scenarioStartTime = config.startTime;
            } else {
                // Fallback: get startTime from regular scenario data
                const selectedScenario = hangoutState.getSelectedScenario();
                if (selectedScenario && selectedScenario.startTime) {
                    scenarioStartTime = selectedScenario.startTime;
                }
            }
        } catch (error) {
            console.warn('message-utils.js: generateInitialGreeting: Error getting scenario startTime:', error);
        }

        // Show location title when entering hangout (non-blocking)
        try {
            const { getLocationTransitionManager } = await import('./location-transition.js');
            const transitionManager = getLocationTransitionManager();
            const { getBackgroundService } = await import('../../../core/services/BackgroundService.js');
            const backgroundService = getBackgroundService();
            const currentBackground = await backgroundService.getCurrentBackground();
            const currentTimeOfDay = hangoutState.getCurrentTimeSlot() || 'night';

            // Show the location title in parallel (don't await - let it run alongside greeting)
            void transitionManager.showLocationTitle(currentBackground.locationId, currentTimeOfDay).catch(error => {
                console.warn('Location title animation failed:', error);
            });

        } catch (error) {
            console.warn('Failed to start location title during hangout initialization:', error);
        }

        // VN turn navigation: seed with greeting roster in ID order (worker streams greetings that way).
        try {
            const displayManager = await getDisplayManager();
            const greetingCharacters = sortGreetingCharactersById(characters);
            if (isVnLayoutMode() && displayManager?.isAvailable?.() && typeof displayManager.setTurnNavigation === 'function') {
                displayManager.setTurnNavigation(greetingCharacters);
            }

            // Ensure VN has per-character placeholders so arrow navigation works immediately,
            // even before queued/manual-paced responses are displayed.
            if (displayManager?.isAvailable?.() && typeof displayManager.createTypingIndicator === 'function') {
                for (const character of greetingCharacters) {
                    const charId = character?.id ?? character?.characterId;
                    const charName = character?.name ?? character?.characterName ?? 'Character';
                    if (!charId) continue;
                    await displayManager.createTypingIndicator(charId, charName, { isGreeting: true });
                }
            }
        } catch (e) {
            console.warn('generateInitialGreeting: failed to set VN turn navigation', e);
        }

        const requestBody = buildGreetingRequest({
            conversationId,
            scenarioId,
            characters: characters,
            currentTime: scenarioStartTime || currentSession?.currentTime || config?.currentTime || '20:20',
            hangoutConfig,
            variation: 0
        });

        const workerUrl = hangoutWorkerUrl('/v1/hangout/greeting');

        const response = await makeWorkerRequest(workerUrl, requestBody, {
            allowUnauthenticated: true
        });

        const data = await processStreamingResponse(response, characters, getBackgroundServiceInstance, {
            isInitialGreeting: true
        });

        const greetingMessages = hangoutState.getMessages() || [];
        const variation = hangoutState.getGreetingVariationMeta().variation;
        hangoutState.setMessages(greetingMessages.map((message) => {
            if (message?.isGreeting !== true && message?.isInitialGreeting !== true) return message;
            return {
                ...message,
                path: resolveGreetingChainPath(message.characterId, characters, variation),
                isGreeting: true,
                isInitialGreeting: true
            };
        }));
        hangoutState.rememberGreetingVariation(variation);
        hangoutState.syncBranchTipToSelectedGreetingRoot(variation);

        // Clean up mobile-specific classes after all greeting responses are complete
        // This ensures all characters reappear and the user can type a new message
        if (window.innerWidth <= 1024 && isVnLayoutMode()) {
            clearConversationSpeakingClasses();
        }

        return data;
    } catch (error) {
        console.error('message-utils.js: generateInitialGreeting: Error generating initial greeting:', error);
        throw error;
    }
}


export async function processChatInput(message) {
    if (!message || typeof message !== 'string') return;

    const hangoutState = HangoutState.getInstance();

    conversationQueue.clearQueue();

    // Reset first response flag for new conversation round
    window._hasStreamedFirstResponse = false;

    document.dispatchEvent(new CustomEvent('hangout:user-message-submitted', {
        detail: {
            message,
            timestamp: Date.now()
        }
    }));

    // Send regular message - typing indicator will be triggered inside sendMessage after auth check
    await sendMessage(message);
}

const hasUserMessageInTranscript = (messages) =>
    Array.isArray(messages) && messages.some((message) => message?.role === 'user');

export function hasAnyUserMessageInHangout() {
    const hangoutState = HangoutState.getInstance();
    return hasUserMessageInTranscript(hangoutState.getMessages());
}

export async function prepareGreetingReplacementForLanguageSwitch({ triggerSource = '' } = {}) {
    if (triggerSource !== 'language-control') {
        return { skipped: true, reason: 'missing-explicit-language-switch-intent' };
    }

    const hangoutState = HangoutState.getInstance();
    const transcript = hangoutState.getMessages();

    if (hasUserMessageInTranscript(transcript)) {
        return { skipped: true, reason: 'has-user-message' };
    }

    hangoutState.clearGreetingVariationCache();
    conversationQueue.clearQueue();
    window._hasStreamedFirstResponse = false;
    window.isInitialGreetingStarting = false;

    cleanupAllAudioCache();

    try {
        const displayManager = await getDisplayManager();
        if (displayManager?.clearActiveMessages) {
            displayManager.clearActiveMessages();
        }
    } catch (error) {
        console.warn('[Hangout] Failed to clear active message bubbles before greeting refresh:', error);
    }

    if (hangoutState.state) {
        hangoutState.state.messages = [];
        hangoutState.state.messageHistory = [];
    }

    const conversationId = hangoutState.getConversationId();
    const previousCount = Array.isArray(transcript) ? transcript.length : 0;
    if (conversationId) {
        try {
            storage.clearOpeningGreetingMessages();
            await hangoutState.setMessages([]);
        } catch (error) {
            console.warn('[Hangout] Failed to clear persisted transcript before greeting refresh:', error);
        }
    }

    return { skipped: false, reason: 'language-control', previousCount };
}


export { initializeConversationRestorationHandler, restoreConversationUI };

initializeConversationRestorationHandler();


export async function syncChatbotVisibleHistory() {
    if (getCurrentLayoutMode() !== LAYOUT_MODES.CHATBOT) return;
    const hangoutState = HangoutState.getInstance();
    const displayManager = await getDisplayManager();
    await displayManager.syncHistory?.(getTranscriptForLayout(hangoutState));
}

const presentAppliedGreetingVariation = async (hangoutState) => {
    await syncChatbotVisibleHistory();
    const displayManager = await getDisplayManager();
    const variation = hangoutState.getGreetingVariationMeta().variation;
    const root = greetingRootSegment(variation);
    const greetings = (hangoutState.getMessages() || []).filter((message) =>
        isGreetingMessage(message) && getPathRootSegment(message?.path) === root
    );
    for (const message of greetings) {
        const characterId = message.characterId ?? message.character_id;
        if (!characterId) continue;
        const bubble = displayManager.findMessageByCharacterId?.(characterId);
        if (bubble) {
            bubble.isTyping = false;
            bubble.isEditing = false;
            bubble.isGreeting = true;
            if (message.path) bubble.path = normalizeMessagePath(message.path);
            bubble.setText?.(message.content || '');
            bubble._renderVersionControls?.();
        }
        const spriteData = resolveMessageSpriteData(message, {
            characterId,
            fallbackOutfitId: message.outfitId ?? 0
        });
        await displayManager.refreshMessageAvatar?.(characterId, {
            actionId: spriteData.actionId,
            outfitId: spriteData.outfitId,
            isVideo: spriteData.isVideo === true
        });
    }
    displayManager.redrawAllMessages?.();
};

const syncSelectedVersionPresentation = async (selectedPath, characterId) => {
    const hangoutState = HangoutState.getInstance();
    const normalizedPath = normalizeMessagePath(selectedPath);
    const normalizedCharacterId = String(characterId ?? '');
    await applyHistoricalHangoutScene(hangoutState.getVisibleMessages(), normalizedPath, {
        allowCurrentLocationFallback: false
    });
    await syncChatbotVisibleHistory();

    const selectedMessage = (hangoutState.getMessages?.() || []).find((message) =>
        normalizeMessagePath(message?.path) === normalizedPath
        && String(message?.characterId ?? message?.character_id ?? '') === normalizedCharacterId
    ) || null;

    const hangoutConfig = await getHangoutConfig() || {};
    const fallbackOutfitId = (hangoutConfig.allCharacters || []).find(
        (character) => String(character?.id) === normalizedCharacterId
    )?.outfitId ?? selectedMessage?.outfitId ?? 0;

    const spriteData = selectedMessage
        ? resolveMessageSpriteData(selectedMessage, {
            characterId: normalizedCharacterId,
            fallbackOutfitId
        })
        : null;
    const spriteOptions = spriteData
        ? {
            actionId: spriteData.actionId,
            outfitId: spriteData.outfitId,
            isVideo: spriteData.isVideo === true
        }
        : null;

    if (getCurrentLayoutMode() === LAYOUT_MODES.CHATBOT && spriteOptions) {
        try {
            const displayManager = await getDisplayManager();
            await displayManager.refreshMessageAvatarByPath?.(
                normalizedPath,
                normalizedCharacterId,
                spriteOptions
            );
        } catch (avatarError) {
            console.warn('syncSelectedVersionPresentation: bubble avatar refresh failed', avatarError);
        }
    }

    await focusHangoutConversationTurn({
        path: normalizedPath,
        characterId: normalizedCharacterId,
        spriteOptions
    });
};

const isEventStreamResponse = (response) =>
    String(response?.headers?.get?.('content-type') || '').includes('text/event-stream');

const sortGreetingCharactersById = (characters = []) =>
    [...(Array.isArray(characters) ? characters : [])].sort(
        (a, b) => compareNumericCharacterIds(a?.id, b?.id)
    );

const prepareGreetingVariationStreamUi = async (characters) => {
    const displayManager = await getDisplayManager();
    if (displayManager?.clearActiveMessages) {
        displayManager.clearActiveMessages();
    }
    await syncChatbotVisibleHistory();

    const greetingCharacters = sortGreetingCharactersById(characters);
    if (isVnLayoutMode() && displayManager?.isAvailable?.() && typeof displayManager.setTurnNavigation === 'function') {
        displayManager.setTurnNavigation(greetingCharacters);
    }

    if (displayManager?.isAvailable?.() && typeof displayManager.createTypingIndicator === 'function') {
        for (const character of greetingCharacters) {
            const charId = character?.id ?? character?.characterId;
            const charName = character?.name ?? character?.characterName ?? 'Character';
            if (!charId) continue;
            await displayManager.createTypingIndicator(charId, charName, { isGreeting: true });
        }
    }
};

const finalizeStreamedGreetingMessages = (hangoutState, characters, variation) => {
    const variationIndex = Number.isFinite(Number(variation))
        ? Number(variation)
        : hangoutState.getGreetingVariationMeta().variation;
    const selectedRoot = greetingRootSegment(variationIndex);
    hangoutState.setMessages((hangoutState.getMessages() || []).map((message) => {
        if (message?.isGreeting !== true && message?.isInitialGreeting !== true) return message;
        const path = normalizeMessagePath(message.path);
        if (isValidLtreePath(path) && getPathRootSegment(path) !== selectedRoot) return message;
        return {
            ...message,
            path: resolveGreetingChainPath(message.characterId, characters, variationIndex),
            isGreeting: true,
            isInitialGreeting: true
        };
    }));
    hangoutState.syncBranchTipToSelectedGreetingRoot(variationIndex);
};

const consumeGreetingVariationStream = async (response, characters, hangoutState, nextVariation) => {
    await prepareGreetingVariationStreamUi(characters);
    await processStreamingResponse(response, characters, getBackgroundServiceInstance, {
        isInitialGreeting: true
    });
    finalizeStreamedGreetingMessages(hangoutState, characters, nextVariation);
    hangoutState.rememberGreetingVariation(nextVariation);
    await syncChatbotVisibleHistory();
    return { success: true, variation: nextVariation };
};

export async function switchGreetingVariation(direction) {
    const hangoutState = HangoutState.getInstance();
    const { variation, count } = hangoutState.getGreetingVariationMeta();
    const delta = direction === 'left' ? -1 : 1;
    const nextVariation = variation + delta;
    if (nextVariation < 0 || nextVariation >= count) {
        return { success: false, reason: 'no_sibling_variation' };
    }

    hangoutState.rememberGreetingVariation(variation);
    hangoutState.setGreetingVariationMeta({ variation: nextVariation, count });
    hangoutState.syncBranchTipToSelectedGreetingRoot(nextVariation);
    const cachedGreeting = hangoutState.getCachedGreetingVariation(nextVariation);
    if (cachedGreeting) {
        hangoutState.applyGreetingVariationMessages(cachedGreeting);
        await presentAppliedGreetingVariation(hangoutState);
        return { success: true, variation: nextVariation };
    }

    const hangoutConfig = await getHangoutConfig() || {};
    const characters = hangoutConfig.allCharacters || [];
    const scenarioId = hangoutConfig.scenarioId || hangoutState.getSelectedScenario()?.id || null;
    const currentSession = hangoutState.getCurrentSession();
    const currentTime = currentSession?.currentTime || hangoutConfig.currentTime || hangoutConfig.startTime || '20:20';
    const conversationId = hangoutState.getConversationId();

    if (isResolvedConversationId(conversationId)) {
        const workerUrl = hangoutWorkerUrl('/v1/hangout/greeting-variation');
        let response;
        try {
            response = await makeWorkerRequest(workerUrl, {
                conversationId,
                variation: nextVariation,
                language: storage.get('displayLanguage') || 'en',
                scenarioId,
                characters,
                currentTime
            });
        } catch (error) {
            await showMessageTreeError(error?.workerError?.message || error?.message || 'Unable to switch greeting variation.');
            return { success: false, error: 'variation_switch_failed' };
        }

        if (isEventStreamResponse(response)) {
            return consumeGreetingVariationStream(response, characters, hangoutState, nextVariation);
        }

        const data = await response.json().catch(() => null);
        if (!response.ok || data?.success !== true) {
            await showMessageTreeError(data?.error || 'Unable to switch greeting variation.');
            return { success: false, error: data?.error || 'variation_switch_failed' };
        }

        hangoutState.setGreetingVariationMeta({
            variation: data.greetingVariation,
            count: data.greetingVariationCount
        });
        hangoutState.applyGreetingVariationMessages(data.messages);
        hangoutState.rememberGreetingVariation(data.greetingVariation ?? nextVariation);
        await presentAppliedGreetingVariation(hangoutState);
        return { success: true, variation: nextVariation };
    }

    if (!scenarioId) {
        await showMessageTreeError('Unable to switch greeting variation.');
        return { success: false, reason: 'missing_scenario' };
    }

    let response;
    try {
        response = await makeWorkerRequest(
            hangoutWorkerUrl('/v1/hangout/greeting'),
            buildGreetingRequest({
                conversationId,
                scenarioId,
                characters,
                currentTime,
                hangoutConfig,
                variation: nextVariation
            }),
            { allowUnauthenticated: true }
        );
    } catch (error) {
        await showMessageTreeError(error?.workerError?.message || error?.message || 'Unable to switch greeting variation.');
        return { success: false, error: 'variation_switch_failed' };
    }

    return consumeGreetingVariationStream(response, characters, hangoutState, nextVariation);
}

export async function navigateAssistantVersion(messagePath, characterId, direction) {
    const hangoutState = HangoutState.getInstance();
    const existing = (hangoutState.getMessages?.() || []).find((message) =>
        normalizeMessagePath(message?.path) === normalizeMessagePath(messagePath)
    );
    if (isGreetingMessage(existing)) {
        return switchGreetingVariation(direction);
    }
    const meta = getVersionMetaForPath(
        hangoutState.getMessages(),
        messagePath,
        characterId,
        hangoutState.getActiveVersions()
    );
    if (!meta.total || meta.total <= 1) {
        if (direction === 'right') {
            return regenerateAssistantMessage({
                path: meta.selectedPath || messagePath,
                characterId,
                canonicalPath: meta.canonicalPath
            });
        }
        return { success: false, reason: 'single_version' };
    }

    if (direction === 'left') {
        if (meta.index <= 1) return { success: false, reason: 'at_first' };
        const previous = meta.versions[meta.index - 2];
        const cohort = selectVersionCohort(hangoutState.getMessages(), previous.path);
        hangoutState.applyActiveVersions(cohort);
        hangoutState.setBranchTipPath(previous.path);
        await syncSelectedVersionPresentation(previous.path, characterId);
        return { success: true, selectedPath: previous.path };
    }

    if (direction === 'right') {
        if (meta.index < meta.total) {
            const next = meta.versions[meta.index];
            const cohort = selectVersionCohort(hangoutState.getMessages(), next.path);
            hangoutState.applyActiveVersions(cohort);
            hangoutState.setBranchTipPath(next.path);
            await syncSelectedVersionPresentation(next.path, characterId);
            return { success: true, selectedPath: next.path };
        }
        return regenerateAssistantMessage({
            path: meta.selectedPath,
            characterId,
            canonicalPath: meta.canonicalPath
        });
    }

    return { success: false, reason: 'invalid_direction' };
}

const showMessageTreeError = async (message) => {
    try {
        const { showError } = await import('../../utils/UnifiedNotificationSystem.js');
        showError(message || 'Unable to update message. Please refresh and try again.', { duration: 6000 });
    } catch (error) {
        console.warn('showMessageTreeError failed:', error);
    }
};

export async function regenerateAssistantMessage({ path, characterId, canonicalPath = null } = {}) {
    const hangoutState = HangoutState.getInstance();
    let messages = hangoutState.getMessages();
    let normalizedPath = normalizeMessagePath(path);
    const normalizedCharacterId = String(characterId ?? '');
    if (!normalizedPath || !normalizedCharacterId) {
        return { success: false, error: 'Missing path or characterId' };
    }

    let targetMessage = messages.find((message) =>
        normalizeMessagePath(message?.path) === normalizedPath
        && String(message?.characterId ?? '') === normalizedCharacterId
    );
    if (!targetMessage) return { success: false, error: 'Message not found' };
    if (isGreetingMessage(targetMessage)) {
        return { success: false, error: 'Greeting versions are local-only' };
    }

    const conversationId = hangoutState.getConversationId();
    if (!isResolvedConversationId(conversationId)) {
        await showMessageTreeError('Missing conversation id. Please refresh and try again.');
        return { success: false, error: 'Missing conversationId' };
    }

    if (!isValidLtreePath(normalizedPath)) {
        const preReconcileSnapshot = {
            localMessages: Array.isArray(messages) ? [...messages] : [],
            localPath: normalizedPath,
            characterId: normalizedCharacterId,
            content: targetMessage.content
        };
        const reconcileResult = await hangoutState.reconcileTranscriptWithRemote(conversationId);
        if (reconcileResult.success) {
            messages = hangoutState.getMessages();
            const remapped = resolveDbPathForLocalAssistant({
                localMessages: preReconcileSnapshot.localMessages,
                dbMessages: reconcileResult.remoteMessages || [],
                localPath: preReconcileSnapshot.localPath,
                characterId: preReconcileSnapshot.characterId,
                content: preReconcileSnapshot.content
            });
            if (remapped) {
                normalizedPath = remapped;
                targetMessage = messages.find((message) =>
                    normalizeMessagePath(message?.path) === normalizedPath
                    && String(message?.characterId ?? '') === normalizedCharacterId
                ) || targetMessage;
            }
        }
    }

    let resolvedCanonical = normalizeMessagePath(canonicalPath)
        || resolveCanonicalPath(normalizedPath, messages);
    const precedingUser = getPrecedingUserMessage(messages, normalizedPath);
    const seedMessage = precedingUser?.content || '';
    if (!seedMessage.trim()) {
        await showMessageTreeError('Could not find the preceding user message for regenerate.');
        return { success: false, error: 'Missing preceding user message' };
    }

    const hangoutConfig = await getHangoutConfig() || {};
    const configCharacters = Array.isArray(hangoutConfig.allCharacters) ? hangoutConfig.allCharacters : [];
    const visibleBranch = hangoutState.getVisibleMessages();
    const roundAssistants = getRoundAssistantMessages(visibleBranch, normalizedPath);
    const restOfTurn = getRestOfTurnAssistants(visibleBranch, normalizedPath);
    const regenMessages = restOfTurn.regen.length > 0
        ? restOfTurn.regen
        : [targetMessage];
    const keptMessages = restOfTurn.kept;
    const regenCharacterIds = new Set(
        regenMessages.map((message) => String(message.characterId ?? '')).filter(Boolean)
    );
    if (!regenCharacterIds.has(normalizedCharacterId)) {
        regenCharacterIds.add(normalizedCharacterId);
    }
    const historicalScene = resolveHistoricalSceneFromMessages(visibleBranch, normalizedPath);
    const roundCharacterIds = [...new Set(
        roundAssistants.map((message) => String(message.characterId ?? '')).filter(Boolean)
    )];
    for (const regenId of regenCharacterIds) {
        if (!roundCharacterIds.includes(regenId)) roundCharacterIds.push(regenId);
    }

    const buildRegenerateResponders = (messageList = messages) => regenMessages.map((message) => {
        const messageCharacterId = String(message?.characterId ?? normalizedCharacterId);
        const messagePath = messageCharacterId === normalizedCharacterId
            ? normalizedPath
            : (normalizeMessagePath(message?.path) || normalizedPath);
        return {
            characterId: messageCharacterId,
            path: messagePath,
            canonicalPath: resolveCanonicalPath(messagePath, messageList)
                || (messageCharacterId === normalizedCharacterId ? resolvedCanonical : messagePath)
        };
    });

    const buildHistoricalCharacters = () => {
        const byId = new Map(configCharacters.map((character) => [String(character.id), character]));
        return roundCharacterIds.map((id) => {
            const configChar = byId.get(id);
            const roundMsg = [...roundAssistants].reverse().find((message) =>
                String(message.characterId ?? '') === id
            );
            const outfitFromRound = roundMsg?.outfitId ?? roundMsg?.outfit_id;
            const outfitFromTarget = regenCharacterIds.has(id)
                ? (id === normalizedCharacterId
                    ? (targetMessage.outfitId ?? targetMessage.outfit_id)
                    : (roundMsg?.outfitId ?? roundMsg?.outfit_id))
                : null;
            const outfitId = outfitFromTarget ?? outfitFromRound ?? configChar?.outfitId ?? 0;
            return {
                id: parseNumericId(id) ?? id,
                outfitId: parseNumericId(outfitId) ?? 0,
                outfitName: configChar?.outfitName || 'default outfit',
                name: configChar?.name || roundMsg?.characterName || targetMessage.characterName || `Character ${id}`,
                shouldRespond: regenCharacterIds.has(id)
            };
        });
    };

    const historicalCharacters = buildHistoricalCharacters();
    const targetCharacter = historicalCharacters.find((character) =>
        String(character.id) === normalizedCharacterId
    ) || configCharacters.find((character) => String(character.id) === normalizedCharacterId);
    if (!targetCharacter) return { success: false, error: 'Character not in session' };

    const regenResponderCharacters = orderRespondingCharacters(
        seedMessage,
        historicalCharacters.filter((character) => regenCharacterIds.has(String(character.id)))
    );
    const hiddenPaths = regenMessages
        .map((message) => normalizeMessagePath(message?.path))
        .filter(Boolean);

    await applyHistoricalHangoutScene(visibleBranch, normalizedPath, {
        scene: historicalScene,
        allowCurrentLocationFallback: false
    });

    const buildRequestBody = async (pathForRegen, canonicalForRegen, messageList = messages) => {
        const regenerateResponders = buildRegenerateResponders(messageList);
        const resolvedHistory = await resolveConversationHistory({ hangoutState });
        const visibleHistory = filterHistoryForRegenerate(resolvedHistory, {
            path: pathForRegen,
            characterId: normalizedCharacterId,
            canonicalPath: canonicalForRegen,
            restOfTurn: {
                kept: keptMessages,
                regen: regenerateResponders.map((responder) => ({
                    path: responder.path,
                    characterId: responder.characterId,
                    canonicalPath: responder.canonicalPath
                }))
            }
        });
        const scenarioId = hangoutConfig.scenarioId || hangoutState.getSelectedScenario()?.id;
        const scene = resolveHistoricalSceneFromMessages(
            hangoutState.getVisibleMessages(),
            pathForRegen
        );
        /** @type {any} */
        const requestBody = {
            message: seedMessage,
            conversationId: String(conversationId).trim(),
            scenarioId: parseNumericId(scenarioId) ?? scenarioId,
            usernameForPrompt: resolveUserNameFromStorage({ preserveToken: true, fallback: '{user}' }),
            history: visibleHistory.filter((message) => message.role !== 'system'),
            regenerateOfPath: pathForRegen,
            canonicalPath: canonicalForRegen,
            regenerateResponders,
            speakingOrder: speakingOrderIds(regenResponderCharacters),
            promptId: null,
            reasoning: null,
            temperature: null,
            characters: buildHistoricalCharacters(),
            language: storage.get('userData')?.language || 'en',
            isPrivateScenario: hangoutConfig.isPrivateScenario === true
        };
        if (scene.locationId != null) {
            requestBody.location = scene.locationId;
        }
        if (scene.currentTime || scene.time != null) {
            requestBody.currentTime = scene.currentTime || formatMilitaryAsClock(scene.time);
            requestBody.stateDiff = {
                current_time: requestBody.currentTime,
                ...(scene.locationId != null ? { loc: scene.locationId, location_id: scene.locationId } : {}),
                ...(scene.time != null ? { time: scene.time } : {})
            };
        } else if (scene.locationId != null) {
            requestBody.stateDiff = {
                loc: scene.locationId,
                location_id: scene.locationId
            };
        }
        const customScenarioDescription = hangoutConfig.customScenarioDescription
            || storage.get('hangout:customScenarioDescription');
        if (typeof customScenarioDescription === 'string' && customScenarioDescription.trim()) {
            requestBody.scenarioDescription = customScenarioDescription.trim();
        }
        return requestBody;
    };

    const displayManager = await getDisplayManager();
    const clearRegenTypingIndicators = async () => {
        for (const character of regenResponderCharacters) {
            await displayManager.removeTypingIndicator?.(character.id);
        }
    };
    const abortPendingBranchUi = async () => {
        hangoutState.clearPendingBranch();
        await clearRegenTypingIndicators();
        await syncChatbotVisibleHistory();
    };
    const showRegenTypingIndicators = async () => {
        if (getCurrentLayoutMode() !== LAYOUT_MODES.CHATBOT) return;
        for (const character of regenResponderCharacters) {
            const responderMeta = buildRegenerateResponders(messages).find((entry) =>
                String(entry.characterId) === String(character.id)
            );
            await displayManager.createTypingIndicator?.(character.id, character.name, {
                path: responderMeta?.path || normalizedPath
            });
        }
    };

    hangoutState.beginPendingBranch(resolvedCanonical, normalizedPath, { hiddenPaths });
    await syncChatbotVisibleHistory();
    if (isVnLayoutMode() && typeof displayManager.setTurnNavigation === 'function') {
        displayManager.setTurnNavigation(regenResponderCharacters);
    }
    await showRegenTypingIndicators();

    const textGenFields = await buildTextGenRequestFields();
    const workerUrl = hangoutWorkerUrl('/message');

    const attemptRegenerate = async (pathForRegen, canonicalForRegen, messageList = messages) => {
        const requestBody = await buildRequestBody(pathForRegen, canonicalForRegen, messageList);
        Object.assign(requestBody, textGenFields);
        if (!textGenFields.customPrompt) delete requestBody.customPrompt;
        applyHangoutMemoryToRequestBody(requestBody, hangoutConfig?.allCharacters || historicalCharacters);
        return makeWorkerRequest(workerUrl, requestBody);
    };

    const remapSnapshot = {
        localMessages: Array.isArray(messages) ? [...messages] : [],
        localPath: normalizedPath,
        characterId: normalizedCharacterId,
        content: targetMessage.content
    };
    let response;
    try {
        response = await attemptRegenerate(normalizedPath, resolvedCanonical);
    } catch (error) {
        const message = String(error?.message || error || '');
        const errorCode = error?.code || error?.workerError?.code || null;
        const shouldReconcile = errorCode === 'version_path_allocation_failed'
            || message.includes('Canonical assistant message not found')
            || message.includes('version_path_allocation_failed')
            || message.includes('Message not found');
        if (!shouldReconcile) {
            await abortPendingBranchUi();
            await showMessageTreeError('Unable to regenerate message. Please try again.');
            return { success: false, error: message };
        }

        const reconcileResult = await hangoutState.reconcileTranscriptWithRemote(conversationId);
        if (!reconcileResult.success) {
            await abortPendingBranchUi();
            await showMessageTreeError('Unable to sync conversation paths. Please refresh and try again.');
            return { success: false, error: 'Reconcile failed' };
        }

        const remappedPath = resolveDbPathForLocalAssistant({
            localMessages: remapSnapshot.localMessages,
            dbMessages: reconcileResult.remoteMessages || [],
            localPath: remapSnapshot.localPath,
            characterId: remapSnapshot.characterId,
            content: remapSnapshot.content
        });
        if (!remappedPath) {
            await abortPendingBranchUi();
            await showMessageTreeError('Unable to regenerate message. Please refresh and try again.');
            return { success: false, error: 'Reconciled path not found' };
        }
        messages = hangoutState.getMessages();
        normalizedPath = remappedPath;
        resolvedCanonical = resolveCanonicalPath(normalizedPath, messages);
        hangoutState.beginPendingBranch(resolvedCanonical, normalizedPath, { hiddenPaths });
        await syncChatbotVisibleHistory();
        await showRegenTypingIndicators();
        try {
            response = await attemptRegenerate(normalizedPath, resolvedCanonical, messages);
        } catch (retryError) {
            await abortPendingBranchUi();
            await showMessageTreeError('Unable to regenerate message. Please refresh and try again.');
            return { success: false, error: String(retryError?.message || retryError) };
        }
    }

    let data;
    try {
        data = await processStreamingResponse(response, historicalCharacters, getBackgroundServiceInstance, {
            isInitialGreeting: false
        });
    } catch (streamError) {
        await abortPendingBranchUi();
        await showMessageTreeError('Unable to regenerate message. Please try again.');
        return { success: false, error: String(streamError?.message || streamError) };
    }

    const latestMessages = hangoutState.getMessages();
    const streamedResponderPaths = [];
    for (const responder of buildRegenerateResponders(latestMessages)) {
        const streamedPath = normalizeMessagePath(
            latestMessages.slice().reverse().find((message) => {
                if (message?.role !== 'assistant') return false;
                if (String(message?.characterId ?? '') !== String(responder.characterId)) return false;
                const messagePath = normalizeMessagePath(message?.path);
                if (!messagePath) return false;
                return resolveCanonicalPath(messagePath, latestMessages) === responder.canonicalPath;
            })?.path
        );
        if (streamedPath && responder.canonicalPath) {
            hangoutState.setActiveVersion(responder.canonicalPath, streamedPath);
            streamedResponderPaths.push(streamedPath);
        }
    }
    const cohortTipPath = streamedResponderPaths.length > 0
        ? streamedResponderPaths[streamedResponderPaths.length - 1]
        : null;
    if (cohortTipPath) {
        hangoutState.applyActiveVersions(selectVersionCohort(latestMessages, cohortTipPath));
        hangoutState.setBranchTipPath(cohortTipPath);
    }
    hangoutState.clearPendingBranch();
    if (cohortTipPath) {
        const tipCharacterId = String(
            latestMessages.find((message) =>
                normalizeMessagePath(message?.path) === cohortTipPath
            )?.characterId ?? normalizedCharacterId
        );
        await syncSelectedVersionPresentation(cohortTipPath, tipCharacterId);
    } else {
        await syncChatbotVisibleHistory();
    }
    return { success: true, data };
}

export async function redoAssistantActionDecision({ path, characterId } = {}) {
    const hangoutState = HangoutState.getInstance();
    const normalizedPath = normalizeMessagePath(path);
    const normalizedCharacterId = String(characterId ?? '');
    if (!normalizedPath || !normalizedCharacterId) {
        await showMessageTreeError('Unable to redo this action decision.');
        return { success: false, error: 'Missing path or characterId' };
    }

    const conversationId = hangoutState.getConversationId();
    if (!isResolvedConversationId(conversationId)) {
        await showMessageTreeError('Missing conversation id. Please refresh and try again.');
        return { success: false, error: 'Missing conversationId' };
    }

    const hangoutConfig = await getHangoutConfig() || {};
    const history = await resolveConversationHistory({ hangoutState });
    const decisionHistory = filterHistoryForRegenerate(history, {
        path: normalizedPath,
        characterId: normalizedCharacterId
    });
    const scene = resolveHistoricalSceneFromMessages(hangoutState.getVisibleMessages(), normalizedPath);
    const { promptId, reasoning } = await buildTextGenRequestFields();
    const spriteSelections = buildSpriteSelections(hangoutConfig.allCharacters || []);
    const spriteSelectionOptions = getSpriteSelectionOptions();
    const requestBody = {
        conversationId: String(conversationId).trim(),
        path: normalizedPath,
        characterId: normalizedCharacterId,
        scenarioId: hangoutConfig.scenarioId || hangoutState.getSelectedScenario()?.id,
        history: (decisionHistory || []).filter((message) => message.role !== 'system'),
        characters: hangoutConfig.allCharacters || [],
        language: storage.get('userData')?.language || 'en',
        isPrivateScenario: hangoutConfig.isPrivateScenario === true,
        promptId,
        reasoning,
        ...(spriteSelections.length > 0 && { spriteSelections }),
        spriteSelectionOptions
    };
    if (scene.locationId != null) requestBody.location = scene.locationId;
    if (scene.currentTime || scene.time != null) {
        requestBody.currentTime = scene.currentTime || formatMilitaryAsClock(scene.time);
    }
    applyHangoutMemoryToRequestBody(requestBody, hangoutConfig.allCharacters || []);

    const workerUrl = hangoutWorkerUrl('/v1/hangout/message/redo-decision');
    let data;
    try {
        const response = await makeWorkerRequest(workerUrl, requestBody);
        data = await response.json();
    } catch (error) {
        await showMessageTreeError(error?.message || 'Unable to redo action decision.');
        return { success: false, error: error?.message };
    }

    if (!data?.success) {
        await showMessageTreeError(data?.error || 'Unable to redo action decision.');
        return { success: false, error: data?.error };
    }

    const messages = [...(hangoutState.getMessages() || [])];
    const messageIndex = messages.findIndex((message) =>
        normalizeMessagePath(message?.path) === normalizedPath
        && String(message?.characterId ?? '') === normalizedCharacterId
    );
    const resolvedActionId = data.actionId ?? (messageIndex >= 0 ? messages[messageIndex].actionId : null);
    const resolvedOutfitId = data.outfitId ?? (messageIndex >= 0 ? messages[messageIndex].outfitId : 0);
    const isVideo = await resolvePlayableIsVideo(normalizedCharacterId, resolvedOutfitId, resolvedActionId);
    if (messageIndex >= 0) {
        messages[messageIndex] = {
            ...messages[messageIndex],
            actionId: data.actionId ?? messages[messageIndex].actionId,
            actionName: data.actionName ?? messages[messageIndex].actionName,
            outfitId: data.outfitId ?? messages[messageIndex].outfitId,
            locationId: data.location ?? messages[messageIndex].locationId,
            isVideo
        };
        hangoutState.setMessages(messages);
    }

    const spriteState = {
        characterId: normalizedCharacterId,
        actionId: data.actionId,
        actionName: data.actionName,
        outfitId: data.outfitId,
        isVideo
    };
    try {
        const displayManager = await getDisplayManager();
        await displayManager.refreshMessageAvatarByPath?.(normalizedPath, normalizedCharacterId, spriteState);
        await focusHangoutConversationTurn({
            path: normalizedPath,
            characterId: normalizedCharacterId,
            spriteOptions: spriteState
        });
    } catch (error) {
        console.warn('redoAssistantActionDecision: sprite refresh failed', error);
    }
    return { success: true, data };
}

export async function continueAssistantMessage({ path, characterId } = {}) {
    const hangoutState = HangoutState.getInstance();
    const normalizedPath = normalizeMessagePath(path);
    const normalizedCharacterId = String(characterId ?? '');
    if (!normalizedPath || !normalizedCharacterId) {
        return { success: false, error: 'Missing path or characterId' };
    }

    const messages = hangoutState.getMessages() || [];
    const targetMessage = messages.find((message) =>
        normalizeMessagePath(message?.path) === normalizedPath
        && String(message?.characterId ?? '') === normalizedCharacterId
    );
    if (!targetMessage || isGreetingMessage(targetMessage)) {
        return { success: false, error: 'Message not found' };
    }

    const conversationId = hangoutState.getConversationId();
    if (!isResolvedConversationId(conversationId)) {
        await showMessageTreeError('Missing conversation id. Please refresh and try again.');
        return { success: false, error: 'Missing conversationId' };
    }

    const hangoutConfig = await getHangoutConfig() || {};
    const configCharacters = Array.isArray(hangoutConfig.allCharacters) ? hangoutConfig.allCharacters : [];
    const continueHistory = filterHistoryForContinue(messages, normalizedPath);
    const continuePrefix = String(targetMessage.content || '');
    const precedingUser = getPrecedingUserMessage(messages, normalizedPath);
    const seedMessage = precedingUser?.content || 'Continue.';
    const textGenFields = await buildTextGenRequestFields();
    const scene = resolveHistoricalSceneFromMessages(hangoutState.getVisibleMessages(), normalizedPath);

    const requestBody = {
        message: seedMessage,
        conversationId: String(conversationId).trim(),
        scenarioId: hangoutConfig.scenarioId || hangoutState.getSelectedScenario()?.id,
        usernameForPrompt: resolveUserNameFromStorage({ preserveToken: true, fallback: '{user}' }),
        history: continueHistory.filter((message) => message.role !== 'system'),
        continueOfPath: normalizedPath,
        isPrivateScenario: hangoutConfig.isPrivateScenario === true,
        continueExisting: {
            content: continuePrefix,
            actionId: targetMessage.actionId ?? targetMessage.action_id ?? null,
            actionName: targetMessage.actionName || null,
            outfitId: targetMessage.outfitId ?? targetMessage.outfit_id ?? null,
            isVideo: targetMessage.isVideo === true
        },
        ...textGenFields,
        characters: mapHangoutRequestCharacters({
            characters: configCharacters,
            respondIds: new Set([normalizedCharacterId]),
            includeShouldRespond: true
        }),
        language: storage.get('userData')?.language || 'en'
    };
    if (scene.locationId != null) requestBody.location = scene.locationId;
    applyHangoutMemoryToRequestBody(requestBody, configCharacters);

    const displayManager = await getDisplayManager();
    const streamSession = beginStreamSession({ userPath: normalizedPath });
    const existingRecord = displayManager.findMessageByCharacterId?.(normalizedCharacterId)
        || null;
    if (existingRecord && normalizeMessagePath(existingRecord.path) === normalizedPath) {
        existingRecord.streamGenerationId = streamSession.id;
        existingRecord.isAwaitingDecision = true;
        existingRecord.setReasoningPreview?.(false);
        existingRecord.isReasoningPreview = false;
        existingRecord._renderVersionControls?.();
    }

    const workerUrl = hangoutWorkerUrl('/v1/hangout/message/continue');
    try {
        const response = await makeWorkerRequest(workerUrl, requestBody);
        await processStreamingResponse(response, configCharacters, getBackgroundServiceInstance, {
            isInitialGreeting: false,
            streamGenerationId: streamSession.id,
            streamUserPath: streamSession.userPath,
            isContinue: true,
            continuePath: normalizedPath,
            continuePrefix
        });
        await syncChatbotVisibleHistory();
        return { success: true };
    } catch (error) {
        await showMessageTreeError(error?.message || 'Unable to continue message.');
        return { success: false, error: error?.message };
    } finally {
        endStreamSession(streamSession.id);
    }
}

export async function saveAssistantMessageEdit({ path, characterId, content, isGreeting = false } = {}) {
    const hangoutState = HangoutState.getInstance();
    const normalizedPath = normalizeMessagePath(path);
    const existing = (hangoutState.getMessages?.() || []).find((message) =>
        normalizeMessagePath(message?.path) === normalizedPath
    );
    const treatAsGreeting = isGreeting === true
        || isGreetingMessage({ path: normalizedPath, isGreeting, isInitialGreeting: isGreeting })
        || isGreetingMessage(existing);
    const result = await hangoutState.updateMessageContent(path, content, {
        persistRemote: !treatAsGreeting,
        extraUpdates: treatAsGreeting
            ? {
                isGreeting: true,
                isInitialGreeting: true
              }
            : {}
    });
    if (!result?.success) {
        await showMessageTreeError(result?.error || 'Unable to save message edit.');
    }
    if (result?.success && getCurrentLayoutMode() === LAYOUT_MODES.CHATBOT) {
        await syncChatbotVisibleHistory();
    }
    return result;
}
